// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState } from 'react';
import { Typography, Button, CircularProgress } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import ConfirmDialog from 'Components/ConfirmDialog';
import { useAppDispatch, useAppSelector } from 'Hooks';
import CreditCardIcon from 'Components/CreditCardIcon';
import { DeleteCreditCardThunk, UpdateDefaultCardThunk } from 'Features/account/subscriptionThunk';
import { GetTaxThunk } from 'Features/order/orderThunk';
import stylesDef from './styles';

const ONLY_CARD_REASON = 'Add another card before removing this one.';
const NOT_REMOVED = 'The card was not removed.';

interface ComponentProps {
  card : ICreditCard;
  additonalCharges?: number;
  setTotalTax?: (arg0: number) => void;
  checkFirstTimeChangeEstimate: (arg0: Function) => void;
  onEditClick: () => void;
}

export const CreditCardInfo: React.FC<ComponentProps> = ({ card, onEditClick, additonalCharges, setTotalTax, checkFirstTimeChangeEstimate }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const [showLoader, setShowLoader] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const { creditCards } = useAppSelector((state) => state.subscription);
  // anyone who can order may add a card and choose the one that is charged; editing and deleting a card belong to
  // Billing (AUT-6571) — the server refuses them too
  const canManageCards = useAppSelector((state) => _ADMIN_ || !!state.auth.resources?.billing);

  const cards = creditCards;
  // the only card on file stays until another is added, so the account always has a way to pay (the server refuses
  // it too); any other card is removed only once the user confirms
  const onlyCard = cards.length <= 1;

  const dispatch = useAppDispatch();

  // the order form shows the charged card's tax (the Billing page passes no setTotalTax): none once the default card
  // is gone, else the new default card's, falling back to 0 for a non-taxable (e.g. international) billing address
  const refreshTax = async () => {
    if (!setTotalTax) return;
    if (card.defaultCard) setTotalTax(0);
    if (cards.length > 1 && cards.some((c) => c.defaultCard)) {
      const result = await dispatch(GetTaxThunk(additonalCharges));
      setTotalTax(result?.success ? result.salesTaxAmount : 0);
    }
  };

  // a removal the server refuses (e.g. another tab already removed the other card) says so, with the server's reason
  const onDeleteClick = async () => {
    setConfirmDelete(false);
    setDeleteError('');
    setShowLoader(true);
    try {
      const result = await dispatch(DeleteCreditCardThunk(card.id));
      if (!result?.success) {
        setDeleteError(result?.message ? `${NOT_REMOVED} ${result.message}` : NOT_REMOVED);
        return;
      }
      await refreshTax();
    } catch {
      setDeleteError(NOT_REMOVED);
    } finally {
      setShowLoader(false);
    }
  };
  const onCancelDelete = () => setConfirmDelete(false);

  const onSetDefaultClick = async (cardId: any) => {
    await dispatch(UpdateDefaultCardThunk(cardId));
    if (additonalCharges > 0) {
      const result = await dispatch(GetTaxThunk(additonalCharges));
      // Always reflect the newly selected card's tax. GetTaxThunk returns no taxable result
      // when the new card's billing address yields no US sales tax (e.g. an international
      // address); fall back to 0 so the previous card's tax isn't left on screen.
      setTotalTax(result?.success ? result.salesTaxAmount : 0);
    }
  };

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.cardIcon}>
        <CreditCardIcon cardType={card.cardType} />
      </Box>
      <Box>
        <Typography variant="body1">{card.number}</Typography>
        <Typography>{`Expires ${card.expDate}`}</Typography>
      </Box>
      <Box sx={styles.buttonsWrapper}>
        {canManageCards && (
          <>
            <Button sx={styles.button} onClick={onEditClick}>Edit</Button>
            <Button sx={styles.button} disabled={onlyCard} onClick={() => setConfirmDelete(true)}>Delete</Button>
          </>
        )}
      </Box>
      <Box />
      <Box>
        <Typography>{card.name}</Typography>
        <Typography>
          {card.address_line1}
          {card.address_line2}
        </Typography>
        <Typography>
          {card.address_city}
          {card.address_state}
          {card.address_zip}
        </Typography>
      </Box>
      <Box sx={styles.defaultButton}>
        {card.defaultCard ? (
          <Typography color="primary">Primary Card</Typography>
        ) : (
          <Button onClick={(e: any) => checkFirstTimeChangeEstimate(() => onSetDefaultClick(card.id))}>Make default</Button>
        )}
      </Box>
      {canManageCards && onlyCard && (
        <Typography variant="body2" color="textSecondary" sx={styles.onlyCardReason}>{ONLY_CARD_REASON}</Typography>
      )}
      {deleteError && (
        <Typography variant="body2" color="error" role="alert" sx={styles.onlyCardReason}>{deleteError}</Typography>
      )}
      {showLoader && (
        <>
          <Box sx={styles.backdrop} />
          <Box sx={styles.progress}>
            <CircularProgress />
          </Box>
        </>
      )}
      <ConfirmDialog
        showDialog={confirmDelete}
        onOk={onDeleteClick}
        onCancel={onCancelDelete}
        contentText={`Remove the card ending in ${card.number.slice(-4)}? It will no longer be available to pay for orders.`}
      />
    </Box>
  );
};

export default CreditCardInfo;
